import { CheckCircle2, CreditCard, ShieldCheck } from "lucide-react";

import { StepShell } from "@/components/application-form/Field";
import { useExamFee } from "@/context/ExamFeeContext";

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4 py-2 border-b border-slate-100 last:border-0">
      <span className="text-sm text-slate-500">{label}</span>

      <span className="text-sm font-medium text-slate-900 text-right">
        {value || "—"}
      </span>
    </div>
  );
}

/**
 * Step 4 — Submit
 * Top: Review of everything the candidate filled.
 * Bottom: Payment (pay first, then "Submit Application" button appears in page footer).
 */
export function SubmitStep({ application, paid }) {
  const { examFee, loading: examFeeLoading } = useExamFee();

  const a = application || {};
  const p = a.personal || {};
  const ac = a.academic || {};
  const ad = a.address || {};
  const pr = a.preferences || {};

  const feeText =
    examFeeLoading || examFee === null
      ? "—"
      : `₹${examFee}`;

  return (
    <StepShell
      title="Review & Submit"
      description="Check your details first, then complete the payment to submit your application."
    >
      {/* ================= REVIEW ================= */}

      <div className="rounded-xl border border-slate-200 p-5">
        <h3 className="font-heading font-bold text-slate-900 mb-2">
          Application
        </h3>

        <Row label="Application No." value={a.applicationNumber} />
        <Row label="Name" value={p.fullName || a.contact?.email} />
        <Row label="Email" value={a.contact?.email} />
        <Row label="Phone" value={a.contact?.phone} />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-200 p-5">
          <h3 className="font-heading font-bold text-slate-900 mb-2">
            Personal
          </h3>

          <Row label="Date of Birth" value={p.dateOfBirth} />
          <Row label="Gender" value={p.gender} />
          <Row label="Category" value={p.category} />
        </div>

        <div className="rounded-xl border border-slate-200 p-5">
          <h3 className="font-heading font-bold text-slate-900 mb-2">
            Academic
          </h3>

          <Row label="College" value={ac.college} />
          <Row label="Course" value={ac.course} />
          <Row label="Graduation" value={ac.graduationYear} />
          <Row label="CGPA" value={ac.cgpa} />
        </div>

        <div className="rounded-xl border border-slate-200 p-5">
          <h3 className="font-heading font-bold text-slate-900 mb-2">
            Address
          </h3>

          <Row label="City" value={ad.city} />
          <Row label="State" value={ad.state} />
          <Row label="Pincode" value={ad.pincode} />
        </div>

        <div className="rounded-xl border border-slate-200 p-5">
          <h3 className="font-heading font-bold text-slate-900 mb-2">
            Preferences
          </h3>

          <Row label="Exam Shift" value={pr.examShift} />
          <Row label="Interview" value={pr.interviewMode || "Virtual"} />
          <Row label="Interest" value={pr.careerInterest} />
        </div>
      </div>

      {/* ================= PAYMENT ================= */}

      <div className="pt-4 border-t border-slate-100">
        <h2 className="font-heading text-xl sm:text-2xl font-bold text-slate-900">
          Examination Fee Payment
        </h2>

        {paid ? (
          <div className="mt-4 flex items-center gap-3 rounded-lg bg-green-50 border border-green-200 p-5">
            <CheckCircle2 className="h-6 w-6 text-green-600 shrink-0" />

            <div>
              <div className="font-semibold text-green-800">
                Payment successful
              </div>

              <div className="text-sm text-green-700">
                {feeText} received. Click “Submit Application” to finish.
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-4 space-y-5">
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 flex items-end justify-between">
              <div>
                <div className="text-sm font-semibold text-slate-600">
                  CET 2027 — Application Fee
                </div>

                <div className="text-xs text-slate-500 mt-1">
                  Non-refundable · inclusive of applicable charges
                </div>
              </div>

              <div className="font-heading text-4xl font-extrabold text-slate-900">
                {feeText}
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="flex items-start gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-slate-900">
                  <CreditCard className="h-5 w-5 text-[#F5A623]" />
                </div>

                <div>
                  <h3 className="font-semibold text-slate-900">
                    Secure Razorpay Payment
                  </h3>

                  <p className="mt-1 text-sm text-slate-500">
                    Pay securely using UPI, Credit Card, Debit Card, Net
                    Banking, or other payment methods supported by Razorpay.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 text-xs text-slate-500">
              <ShieldCheck className="h-4 w-4 text-green-600 shrink-0" />

              <span>
                Your payment is securely processed by Razorpay. We do not store
                your card or banking details.
              </span>
            </div>
          </div>
        )}
      </div>
    </StepShell>
  );
}